import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ChartLine,
  ArrowDownRight,
  ArrowUpRight,
  Bank,
  UsersThree,
  CalendarCheck,
  CalendarX,
  Briefcase,
  Clock,
  FileText,
  SignOut,
  List,
  X,
  UserGear,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Panel", icon: ChartLine, tid: "nav-dashboard" },
  { to: "/gelirler", label: "Gelirler", icon: ArrowDownRight, tid: "nav-income" },
  { to: "/giderler", label: "Giderler", icon: ArrowUpRight, tid: "nav-expense" },
  { to: "/banka", label: "Banka Hesapları", icon: Bank, tid: "nav-bank" },
  { to: "/cariler", label: "Cari Hesaplar", icon: UsersThree, tid: "nav-contacts" },
  { to: "/tahsilatlar", label: "Yaklaşan Tahsilatlar", icon: CalendarCheck, tid: "nav-receivables" },
  { to: "/odemeler", label: "Yaklaşan Ödemeler", icon: CalendarX, tid: "nav-payments" },
  { to: "/calisanlar", label: "Çalışanlar", icon: Briefcase, tid: "nav-employees" },
  { to: "/mesai", label: "Ek Mesai", icon: Clock, tid: "nav-overtime" },
  { to: "/raporlar", label: "Raporlar", icon: FileText, tid: "nav-reports" },
];

const ADMIN_NAV = [
  { to: "/kullanicilar", label: "Kullanıcılar", icon: UserGear, tid: "nav-users" },
];

export default function Layout({ children }) {
  const { user, isAdmin, logout } = useAuth();
  const items = isAdmin ? [...NAV, ...ADMIN_NAV] : NAV;
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const renderNav = (onNavigate) => (
    <>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {items.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.tid}
              onClick={onNavigate}
              className={({ isActive }) =>
                `mb-1 flex items-center gap-3 rounded-md border px-3 py-2 text-sm transition-colors duration-150 ${
                  isActive
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-transparent text-foreground hover:border-border hover:bg-secondary"
                }`
              }
            >
              <Icon size={18} weight="duotone" />
              <span>{n.label}</span>
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-border px-4 py-4">
        <div className="text-xs text-muted-foreground">Oturum</div>
        <div className="mt-1 truncate text-sm font-medium" data-testid="sidebar-user-email">
          {user?.email}
        </div>
        <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground" data-testid="user-role-badge">
          {isAdmin ? "Yönetici" : "Salt Okunur"}
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-sm font-medium text-foreground transition-colors duration-150 hover:bg-secondary"
        >
          <SignOut size={16} />
          Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop sidebar */}
      <aside
        className="hidden w-64 shrink-0 flex-col border-r border-border bg-white md:flex"
        data-testid="sidebar"
      >
        <div className="border-b border-border px-6 py-5">
          <div className="overline">Finans Yönetimi</div>
          <div className="mt-1 font-black text-xl tracking-tight">NAKİT.</div>
        </div>
        {renderNav(undefined)}
      </aside>

      {/* Mobile top bar */}
      <header className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-white px-4 md:hidden">
        <button
          onClick={() => setMenuOpen(true)}
          data-testid="mobile-menu-btn"
          className="rounded-md border border-border p-1.5 hover:bg-secondary"
          aria-label="Menüyü aç"
        >
          <List size={20} />
        </button>
        <div className="font-black text-lg tracking-tight">NAKİT.</div>
        <div className="w-8" />
      </header>

      {/* Mobile drawer + overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setMenuOpen(false)}
          data-testid="mobile-menu-overlay"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border bg-white transition-transform duration-200 ease-out md:hidden ${
          menuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="mobile-sidebar"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-4">
          <div>
            <div className="overline">Finans Yönetimi</div>
            <div className="mt-1 font-black text-lg tracking-tight">NAKİT.</div>
          </div>
          <button
            onClick={() => setMenuOpen(false)}
            data-testid="mobile-menu-close"
            className="rounded-md border border-border p-1.5 hover:bg-secondary"
            aria-label="Menüyü kapat"
          >
            <X size={16} />
          </button>
        </div>
        {renderNav(() => setMenuOpen(false))}
      </aside>

      <main className="flex-1 overflow-y-auto pt-14 md:pt-0" data-testid="main-content">
        {children}
      </main>
    </div>
  );
}
